'use client';

import React, { createContext, useContext } from 'react';
import { useQuery } from '@tanstack/react-query';
import { CategoriesApi, CategoryPopulated } from '@/lib/Apis';
import { AXIOS_CONFIG } from '@/lib/Apis/wrapper';

interface CategoriesContextValue {
    categories: CategoryPopulated[];
    isLoading: boolean;
    error: Error | null;
    refetch: () => Promise<void>;
}

const CategoriesContext = createContext<CategoriesContextValue | undefined>(undefined);

export function useCategories() {
    const context = useContext(CategoriesContext);
    if (!context) {
        throw new Error('useCategories must be used within CategoriesProvider');
    }
    return context;
}

interface CategoriesProviderProps {
    children: React.ReactNode;
}

export function CategoriesProvider({ children }: CategoriesProviderProps) {
    const { data, isLoading, error, refetch } = useQuery({
        queryKey: ['categories'],
        queryFn: async () => {
            const response = await new CategoriesApi(AXIOS_CONFIG).getAllCategories();
            const all = response.data?.data || [];
            return all.filter((cat: CategoryPopulated) => (cat as any).isActive === true);
        },
        staleTime: 0,
    });

    const handleRefetch = async () => {
        await refetch();
    };

    const value: CategoriesContextValue = {
        categories: data || [],
        isLoading,
        error: error as Error | null,
        refetch: handleRefetch,
    };

    return (
        <CategoriesContext.Provider value={value}>
            {children}
        </CategoriesContext.Provider>
    );
}
